import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthContext";
import { supabase, SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { playSupportNotificationSound } from "@/lib/supportSound";
import { SupportContext, type SupportContextValue } from "./SupportContext";
import type {
  SupportMarkReadRequest,
  SupportMessageView,
  SupportSendMessageRequest,
  SupportSendMessageResponse,
  SupportSessionView,
} from "./types";

interface RefreshSupportStateOptions {
  showLoading?: boolean;
}

const SUPPORT_MESSAGE_PROJECTION = "id,sender_type,content,created_at";
const SUPPORT_SESSION_PROJECTION =
  "id,unread_operator_count,customer_last_seen_at,last_customer_message_at,last_operator_message_at";

const sortMessages = (messages: SupportMessageView[]) =>
  [...messages].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

const upsertMessage = (messages: SupportMessageView[], next: SupportMessageView) => {
  const existingIndex = messages.findIndex((message) => message.id === next.id);
  if (existingIndex === -1) {
    return sortMessages([...messages, next]);
  }

  const merged = [...messages];
  merged[existingIndex] = { ...merged[existingIndex], ...next };
  return sortMessages(merged);
};

function deriveSourceLabel(pathname: string) {
  if (pathname === "/") return "home";
  if (matchPath("/applications/:osId/run", pathname) || matchPath("/operating-systems/:osId/run", pathname)) return "operating-system-run";
  if (matchPath("/applications/:osId", pathname) || matchPath("/operating-systems/:osId", pathname)) return "operating-system";
  if (pathname.startsWith("/applications") || pathname.startsWith("/operating-systems")) return "operating-systems";
  if (pathname.startsWith("/chat/")) return "chat";
  if (pathname.startsWith("/lenses")) return "lenses";
  if (pathname.startsWith("/lens/")) return "lens-profile";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (pathname.startsWith("/account")) return "account";
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/become_expert") || pathname.startsWith("/for_expert")) return "experts";
  return "page";
}

function deriveSendPayload(pathname: string, search: string): Omit<SupportSendMessageRequest, "clientMessageId" | "text"> {
  const params = new URLSearchParams(search);
  const chatMatch = matchPath("/chat/:conversationId", pathname);
  const routeConversationId = chatMatch?.params.conversationId === "new" ? null : chatMatch?.params.conversationId ?? null;
  const operatingSystemRunMatch = matchPath("/applications/:osId/run", pathname)
    ?? matchPath("/operating-systems/:osId/run", pathname);
  const operatingSystemDetailMatch = matchPath("/applications/:osId", pathname)
    ?? matchPath("/operating-systems/:osId", pathname);
  const operatingSystemId = operatingSystemRunMatch?.params.osId ?? operatingSystemDetailMatch?.params.osId ?? null;

  return {
    sourcePath: pathname,
    sourceLabel: deriveSourceLabel(pathname),
    context: {
      search: search || null,
      thinkingMethodId: params.get("method") ?? null,
      conversationId: params.get("conversation") ?? routeConversationId,
      operatingSystemId,
    },
  };
}

const isPageVisible = () =>
  typeof document !== "undefined" && document.visibilityState === "visible" && document.hasFocus();

async function invokeSupportFunction<TResponse>(functionName: string, body: unknown): Promise<TResponse> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    throw new Error(sessionError.message);
  }

  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error("No active session. Please sign in again.");
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = payload?.error || payload?.message || `Support function failed with status ${response.status}`;
    throw new Error(message);
  }

  return payload as TResponse;
}

export const SupportProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { user, loading: authLoading, isAnonymous } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [session, setSession] = useState<SupportSessionView | null>(null);
  const [messages, setMessages] = useState<SupportMessageView[]>([]);

  const sessionRef = useRef<SupportSessionView | null>(null);
  const openRef = useRef(false);
  const markReadPromiseRef = useRef<Promise<void> | null>(null);
  const activeToastRef = useRef<{ dismiss: () => void } | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    openRef.current = isOpen;
  }, [isOpen]);

  const loadMessages = useCallback(async (sessionId: string) => {
    const { data, error } = await supabase
      .from("support_messages")
      .select(SUPPORT_MESSAGE_PROJECTION)
      .eq("session_id", sessionId)
      .order("created_at", { ascending: true });

    if (error) {
      throw new Error(error.message);
    }

    setMessages(sortMessages((data ?? []) as SupportMessageView[]));
  }, []);

  const refreshSupportState = useCallback(async ({ showLoading = false }: RefreshSupportStateOptions = {}) => {
    if (!user) {
      sessionRef.current = null;
      setSession(null);
      setMessages([]);
      setLoading(false);
      return null;
    }

    if (showLoading) {
      setLoading(true);
    }

    const { data, error } = await supabase
      .from("support_sessions")
      .select(SUPPORT_SESSION_PROJECTION)
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("[support] failed to fetch session", error);
      if (showLoading) {
        setLoading(false);
      }
      return null;
    }

    const nextSession = (data as SupportSessionView | null) ?? null;
    sessionRef.current = nextSession;
    setSession(nextSession);

    if (nextSession) {
      try {
        await loadMessages(nextSession.id);
      } catch (messageError) {
        console.error("[support] failed to fetch messages", messageError);
      }
    } else {
      setMessages([]);
    }

    if (showLoading) {
      setLoading(false);
    }

    return nextSession;
  }, [loadMessages, user]);

  useEffect(() => {
    if (authLoading) return;
    void refreshSupportState({ showLoading: true });
  }, [authLoading, refreshSupportState]);

  const markRead = useCallback(async () => {
    const currentSession = sessionRef.current;
    if (!currentSession?.id) return;

    if (markReadPromiseRef.current) {
      return markReadPromiseRef.current;
    }

    const promise = invokeSupportFunction<{ session?: { customer_last_seen_at?: string | null } }>(
      "support-mark-read",
      { sessionId: currentSession.id } satisfies SupportMarkReadRequest,
    )
      .then((data) => {

        const nextSeenAt = data?.session?.customer_last_seen_at ?? new Date().toISOString();
        setSession((prev) =>
          prev && prev.id === currentSession.id
            ? { ...prev, unread_operator_count: 0, customer_last_seen_at: nextSeenAt }
            : prev,
        );
      })
      .catch((error) => {
        console.error("[support] failed to mark read", error);
      })
      .finally(() => {
        markReadPromiseRef.current = null;
      });

    markReadPromiseRef.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`support_sessions:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "support_sessions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            sessionRef.current = null;
            setSession(null);
            setMessages([]);
            return;
          }

          const nextSession = payload.new as SupportSessionView;
          const previousSession = sessionRef.current;
          sessionRef.current = nextSession;
          setSession(nextSession);

          const shouldReloadMessages = !previousSession
            || nextSession.id !== previousSession.id
            || nextSession.last_customer_message_at !== previousSession.last_customer_message_at
            || nextSession.last_operator_message_at !== previousSession.last_operator_message_at;

          if (shouldReloadMessages && nextSession.id) {
            void loadMessages(nextSession.id).catch((error) => {
              console.error("[support] failed to refresh messages after session change", error);
            });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadMessages, user]);

  useEffect(() => {
    if (!user) return;

    const refreshFromServer = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }

      void refreshSupportState();
    };

    window.addEventListener("focus", refreshFromServer);
    window.addEventListener("online", refreshFromServer);
    document.addEventListener("visibilitychange", refreshFromServer);

    return () => {
      window.removeEventListener("focus", refreshFromServer);
      window.removeEventListener("online", refreshFromServer);
      document.removeEventListener("visibilitychange", refreshFromServer);
    };
  }, [refreshSupportState, user]);

  useEffect(() => {
    if (!session?.id) return;

    const channel = supabase
      .channel(`support_messages:${session.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_messages",
          filter: `session_id=eq.${session.id}`,
        },
        (payload) => {
          const nextMessage = payload.new as SupportMessageView;
          setMessages((prev) => upsertMessage(prev, nextMessage));

          if (nextMessage.sender_type !== "operator") {
            return;
          }

          if (openRef.current && isPageVisible()) {
            void markRead();
            return;
          }

          activeToastRef.current?.dismiss();
          const handle = toast({
            title: "Applyt support",
            description: nextMessage.content.length > 120
              ? `${nextMessage.content.slice(0, 117)}...`
              : nextMessage.content,
            onClick: () => {
              setIsOpen(true);
              void markRead();
              activeToastRef.current?.dismiss();
              activeToastRef.current = null;
            },
            className: "cursor-pointer",
          });
          activeToastRef.current = handle;
          playSupportNotificationSound();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [markRead, session?.id, toast]);

  useEffect(() => {
    if (!isOpen || !session?.id) return;

    activeToastRef.current?.dismiss();
    activeToastRef.current = null;

    const maybeMarkRead = () => {
      if (isPageVisible()) {
        void refreshSupportState().then((nextSession) => {
          if (nextSession?.id) {
            return markRead();
          }
        });
      }
    };

    maybeMarkRead();
    window.addEventListener("focus", maybeMarkRead);
    document.addEventListener("visibilitychange", maybeMarkRead);

    return () => {
      window.removeEventListener("focus", maybeMarkRead);
      document.removeEventListener("visibilitychange", maybeMarkRead);
    };
  }, [isOpen, markRead, refreshSupportState, session?.id]);

  const sendMessage = useCallback(async (args: { text: string; clientMessageId: string }) => {
    setSending(true);

    try {
      const payload = {
        ...deriveSendPayload(location.pathname, location.search),
        clientMessageId: args.clientMessageId,
        text: args.text,
      } satisfies SupportSendMessageRequest;

      const response = await invokeSupportFunction<SupportSendMessageResponse>("support-send-message", payload);
      const customerMessage: SupportMessageView = {
        id: response.message.id,
        sender_type: "customer",
        content: response.message.content,
        created_at: response.message.createdAt,
      };

      setMessages((prev) => upsertMessage(prev, customerMessage));

      const { data: sessionData, error: sessionError } = await supabase
        .from("support_sessions")
        .select(SUPPORT_SESSION_PROJECTION)
        .eq("id", response.sessionId)
        .maybeSingle();

      if (sessionError) {
        throw sessionError;
      }

      if (sessionData) {
        sessionRef.current = sessionData as SupportSessionView;
        setSession(sessionData as SupportSessionView);
      }
    } catch (error) {
      console.error("[support] failed to send message", error);
      throw error;
    } finally {
      setSending(false);
    }
  }, [location.pathname, location.search]);

  const openWidget = useCallback(() => setIsOpen(true), []);
  const closeWidget = useCallback(() => setIsOpen(false), []);
  const toggleWidget = useCallback(() => setIsOpen((prev) => !prev), []);

  const value = useMemo<SupportContextValue>(() => ({
    enabled: !authLoading && !!user && !isAnonymous,
    loading,
    sending,
    isOpen,
    unreadCount: session?.unread_operator_count ?? 0,
    session,
    messages,
    openWidget,
    closeWidget,
    toggleWidget,
    sendMessage,
    markRead,
  }), [authLoading, closeWidget, isAnonymous, isOpen, loading, markRead, messages, openWidget, sending, sendMessage, session, toggleWidget, user]);

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
};
